import { Timestamp } from '@ts/shared/date-time/util-core';
import { ShippingMethodCode } from '@ts/shipping/shared/util-core';

import { Order } from './order.model';
import { OrderStatus, OrderStatusEvent } from './order-status.model';

/**
 * The customer's step tracker (#11398 D3), mapped from today's statuses.
 * Courier and pickup follow D3; a till order (point of sales) is paid and
 * done at the counter, so it has those two steps only.
 */
export type OrderProgressFlow = 'courier' | 'pickup' | 'store';

// The steps an order goes through, one of a flow's.
export type OrderProgressNormalStepKey =
  | 'paid'
  | 'preparing'
  | 'awaitingCourier'
  | 'onTheWay'
  | 'readyForPickup'
  | 'complete';

// A stop gets a step of its own, after the last step reached, so the customer
// sees where it stopped (#11398).
export type OrderProgressFailureStepKey = 'shippingFailure' | 'cancelled';

export type OrderProgressStepKey =
  | OrderProgressNormalStepKey
  | OrderProgressFailureStepKey;

export type OrderProgressStepState = 'done' | 'current' | 'upcoming' | 'failed';

export interface OrderProgressStepBase {
  key: OrderProgressStepKey;
  state: OrderProgressStepState;
  // When the order reached the step; null for a step it skipped or hasn't
  // reached.
  timestamp: Timestamp | null;
}

export type OrderProgressStep = Readonly<OrderProgressStepBase>;

/**
 * Where the order stands, which picks the status card's sentence and action.
 * `payment` is before payment (no tracker); `cancelled` has no tracker when
 * it was cancelled before being paid.
 */
export type OrderProgressStage =
  | 'payment'
  | 'paymentFailure'
  | 'preparing'
  | 'awaitingCourier'
  | 'onTheWay'
  | 'readyForPickup'
  | 'complete'
  | 'shippingFailure'
  | 'cancelled';

export interface OrderProgressBase {
  stage: OrderProgressStage;
  flow: OrderProgressFlow;
  steps: readonly OrderProgressStep[];
  // When the order entered its current status, if a change records it.
  stageTimestamp: Timestamp | null;
}

export type OrderProgress = Readonly<OrderProgressBase>;

const FLOW_STEP_KEYS: Readonly<
  Record<OrderProgressFlow, readonly OrderProgressNormalStepKey[]>
> = {
  courier: ['paid', 'preparing', 'awaitingCourier', 'onTheWay', 'complete'],
  pickup: ['paid', 'preparing', 'readyForPickup', 'complete'],
  store: ['paid', 'complete'],
};

/**
 * The step tracker for an order (#11398 D3). Its current status drives the
 * current step, so a Shipping failure sent back to Ready for shipping moves
 * the tracker back; the status changes give each step its time.
 */
export function getOrderProgress(
  order: Order,
  orderStatusEvents: readonly OrderStatusEvent[],
): OrderProgress {
  const flow = getOrderProgressFlow(
    order.shipping_info.shipping_method_with_details.shipping_method.code,
  );
  const orderStatusEventsByTime = [...orderStatusEvents].sort(
    (left, right) => left.date_created_timestamp - right.date_created_timestamp,
  );
  const latestOrderStatusEvent =
    orderStatusEventsByTime[orderStatusEventsByTime.length - 1];
  const stageTimestamp =
    latestOrderStatusEvent?.status_after === order.status
      ? latestOrderStatusEvent.date_created_timestamp
      : null;
  const stepKeys = FLOW_STEP_KEYS[flow];

  const progress = (
    stage: OrderProgressStage,
    steps: readonly OrderProgressStep[],
  ): OrderProgress => ({ stage, flow, steps, stageTimestamp });

  switch (order.status) {
    case 'Ready for payment':
    case 'Awaiting payment':
      return progress('payment', []);
    case 'Payment failure':
      return progress('paymentFailure', []);
    case 'Paid':
    case 'Ready for shipping':
    case 'Awaiting shipping':
    case 'In shipping':
    case 'Complete': {
      const currentIndex = getStepIndex(flow, order.status);
      return progress(
        getProgressingStage(flow, order.status),
        stepKeys.map((key, index) => ({
          key,
          state:
            index < currentIndex
              ? 'done'
              : index === currentIndex
                ? 'current'
                : 'upcoming',
          timestamp:
            index <= currentIndex
              ? getStepTimestamp(flow, key, orderStatusEventsByTime)
              : null,
        })),
      );
    }
    case 'Shipping failure':
      return progress(
        'shippingFailure',
        getFailedSteps(
          'shippingFailure',
          flow,
          orderStatusEventsByTime,
          stageTimestamp,
        ),
      );
    case 'Cancelled':
      return progress(
        'cancelled',
        getFailedSteps(
          'cancelled',
          flow,
          orderStatusEventsByTime,
          stageTimestamp,
        ),
      );
  }
}

/**
 * The steps reached before the stop, all done, then the stop's own step; none
 * when it stopped before payment. A retried delivery is back on a normal
 * status, so it never comes here.
 */
function getFailedSteps(
  failureStepKey: OrderProgressFailureStepKey,
  flow: OrderProgressFlow,
  orderStatusEventsByTime: readonly OrderStatusEvent[],
  failureTimestamp: Timestamp | null,
): readonly OrderProgressStep[] {
  const lastReachedStatus = [...orderStatusEventsByTime]
    .reverse()
    .map((orderStatusEvent) => orderStatusEvent.status_after)
    .find(isOrderStatusProgressing);
  if (!lastReachedStatus) {
    return [];
  }
  const stepKeys = FLOW_STEP_KEYS[flow];
  const reachedIndex = getReachedStepIndex(flow, lastReachedStatus);
  const reachedSteps = stepKeys.slice(0, reachedIndex + 1).map(
    (key): OrderProgressStep => ({
      key,
      state: 'done',
      timestamp: getStepTimestamp(flow, key, orderStatusEventsByTime),
    }),
  );
  const failureStep: OrderProgressStep = {
    key: failureStepKey,
    state: 'failed',
    timestamp: failureTimestamp,
  };
  const stepsAfter = hasStepsAfterFailure(failureStepKey)
    ? stepKeys.slice(reachedIndex + 1).map(
        (key): OrderProgressStep => ({
          key,
          state: 'upcoming',
          timestamp: null,
        }),
      )
    : [];
  return [...reachedSteps, failureStep, ...stepsAfter];
}

function getOrderProgressFlow(
  shippingMethodCode: ShippingMethodCode,
): OrderProgressFlow {
  switch (shippingMethodCode) {
    case 'biteship:grab:instant':
    case 'biteship:grab:instant_car':
    case 'biteship:lalamove:mpv':
    case 'biteship:lalamove:truck':
    case 'biteship:lalamove:van':
      return 'courier';
    case 'self-pickup':
      return 'pickup';
    case 'point-of-sales':
      return 'store';
  }
}

// Statuses past payment and short of a stop: they place the order on a step.
type OrderStatusProgressing =
  | 'Paid'
  | 'Ready for shipping'
  | 'Awaiting shipping'
  | 'In shipping'
  | 'Complete';

function isOrderStatusProgressing(
  orderStatus: OrderStatus,
): orderStatus is OrderStatusProgressing {
  return (
    orderStatus === 'Paid' ||
    orderStatus === 'Ready for shipping' ||
    orderStatus === 'Awaiting shipping' ||
    orderStatus === 'In shipping' ||
    orderStatus === 'Complete'
  );
}

function getProgressingStage(
  flow: OrderProgressFlow,
  orderStatus: OrderStatusProgressing,
): OrderProgressStage {
  if (orderStatus === 'Complete') {
    return 'complete';
  }
  const stepKey = getStepKey(flow, orderStatus);
  switch (stepKey) {
    case 'awaitingCourier':
    case 'onTheWay':
    case 'readyForPickup':
      return stepKey;
    // A till order is prepared until it completes at the counter.
    case 'preparing':
    case 'complete':
      return 'preparing';
  }
}

// The current step's index; Complete is past the last step, so all are done.
function getStepIndex(
  flow: OrderProgressFlow,
  orderStatus: OrderStatusProgressing,
): number {
  const stepKeys = FLOW_STEP_KEYS[flow];
  if (orderStatus === 'Complete') {
    return stepKeys.length;
  }
  return stepKeys.indexOf(getStepKey(flow, orderStatus));
}

function hasStepsAfterFailure(
  failureStepKey: OrderProgressFailureStepKey,
): boolean {
  switch (failureStepKey) {
    // Staff may send it back to Ready for shipping: the rest is still ahead.
    case 'shippingFailure':
      return true;
    case 'cancelled':
      return false;
  }
}

// The last step a status reached: its own step, but the last step only once
// Complete (a till order's Paid is on its way to complete, not there).
function getReachedStepIndex(
  flow: OrderProgressFlow,
  orderStatus: OrderStatusProgressing,
): number {
  const stepKeys = FLOW_STEP_KEYS[flow];
  return orderStatus === 'Complete'
    ? stepKeys.length - 1
    : Math.min(getStepIndex(flow, orderStatus), stepKeys.length - 2);
}

// A status past payment is never on the Dibayar step itself.
function getStepKey(
  flow: OrderProgressFlow,
  orderStatus: Exclude<OrderStatusProgressing, 'Complete'>,
): Exclude<OrderProgressNormalStepKey, 'paid'> {
  if (flow === 'store') {
    return 'complete';
  }
  switch (orderStatus) {
    // Staff are picking between Paid and Ready for shipping (#11398 D3).
    case 'Paid':
    case 'Ready for shipping':
      return 'preparing';
    case 'Awaiting shipping':
      return flow === 'pickup' ? 'readyForPickup' : 'awaitingCourier';
    case 'In shipping':
      return flow === 'pickup' ? 'readyForPickup' : 'onTheWay';
  }
}

// The latest change into a status of the step; a skipped step has none.
function getStepTimestamp(
  flow: OrderProgressFlow,
  stepKey: OrderProgressNormalStepKey,
  orderStatusEventsByTime: readonly OrderStatusEvent[],
): Timestamp | null {
  const reachingEvents = orderStatusEventsByTime.filter((orderStatusEvent) =>
    isStepReachedBy(flow, stepKey, orderStatusEvent.status_after),
  );
  return (
    reachingEvents[reachingEvents.length - 1]?.date_created_timestamp ?? null
  );
}

function isStepReachedBy(
  flow: OrderProgressFlow,
  stepKey: OrderProgressNormalStepKey,
  orderStatus: OrderStatus,
): boolean {
  switch (stepKey) {
    case 'paid':
      return orderStatus === 'Paid';
    case 'complete':
      return orderStatus === 'Complete';
    case 'preparing':
    case 'awaitingCourier':
    case 'onTheWay':
    case 'readyForPickup':
      return (
        isOrderStatusProgressing(orderStatus) &&
        orderStatus !== 'Complete' &&
        getStepKey(flow, orderStatus) === stepKey
      );
  }
}
